import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { getTransactionsPendingApprovalAsync } from '../transactionMandateV2/getPendingTransactionsSlice';

interface IBulkDeclineTransactionRequest {
  approvalIds: string[];
  pin: string;
  reason: string;
}

interface IBulkDeclineTransactionResponse {
  status?: boolean;
  message?: string;
  data?: unknown;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IBulkDeclineTransactionResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const bulkDeclineTransactionAsync = createAsyncThunk(
  'transaction/bulk/decline',
  async (
    values: IBulkDeclineTransactionRequest,
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IBulkDeclineTransactionResponse>(
        'transaction/bulk/reject',
        {
          pin: values.pin,
          reason: values.reason,
          approvalIds: values.approvalIds,
        },
        config,
      );

      dispatch(getTransactionsPendingApprovalAsync({}));

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Transaction successfully approved',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IBulkDeclineTransactionResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const bulkTransactionMandateCreationSlice = createSlice({
  name: 'transaction/bulk/decline',
  initialState,
  reducers: {
    resetTransactionBulkDecline(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bulkDeclineTransactionAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(bulkDeclineTransactionAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IBulkDeclineTransactionResponse;
      })
      .addCase(bulkDeclineTransactionAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IBulkDeclineTransactionResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetTransactionBulkDecline } =
  bulkTransactionMandateCreationSlice.actions;
export default bulkTransactionMandateCreationSlice.reducer;
